import React from 'react';
import { Helmet } from 'react-helmet-async';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import {
  ArrowDown,
  ArrowUp,
  Building2,
  CalendarDays,
  Dot,
  LineChart,
  TrendingDown,
  TrendingUp
} from 'lucide-react';
import { useLearnStocksDaily } from '../hooks/useLearnStocksDaily';
import { Card, CardContent } from '../components/ui/Card';
import { cn } from '../utils/cn';

function formatPrice(price: number): string {
  return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPercent(pct: number): string {
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

function formatGameDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

function SurfaceCard({
  className,
  children
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Card
      className={cn(
        'border-white/60 bg-white/70 shadow-[0_20px_60px_rgba(56,44,20,0.08)] backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.06] dark:shadow-[0_20px_60px_rgba(0,0,0,0.22)]',
        className
      )}
    >
      {children}
    </Card>
  );
}

function InsightList({
  title,
  items,
  icon,
  tone
}: {
  title: string;
  items: string[];
  icon: React.ReactNode;
  tone: 'up' | 'down';
}) {
  const tones = {
    up: {
      heading: 'text-emerald-800 dark:text-emerald-300',
      badge: 'bg-emerald-500/10 text-emerald-700 ring-1 ring-emerald-500/15 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/20',
      dot: 'text-emerald-500'
    },
    down: {
      heading: 'text-rose-800 dark:text-rose-300',
      badge: 'bg-rose-500/10 text-rose-700 ring-1 ring-rose-500/15 dark:bg-rose-400/10 dark:text-rose-300 dark:ring-rose-400/20',
      dot: 'text-rose-500'
    }
  } as const;

  const style = tones[tone];

  return (
    <SurfaceCard className="h-full rounded-none">
      <CardContent className="p-6">
        <div className="mb-5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className={cn('mb-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em]', style.badge)}>
              {icon}
              {tone === 'up' ? 'Bull Case' : 'Risk Watch'}
            </div>
            <h2 className={cn('font-serif text-xl font-semibold tracking-tight', style.heading)}>{title}</h2>
          </div>
        </div>

        <ul className="space-y-3.5">
          {items.map((item, index) => (
            <li key={index} className="flex gap-3 text-[15px] leading-7 text-gray-700 dark:text-gray-300">
              <Dot className={cn('mt-1 h-5 w-5 shrink-0', style.dot)} />
              <span dangerouslySetInnerHTML={{ __html: item }} />
            </li>
          ))}
        </ul>
      </CardContent>
    </SurfaceCard>
  );
}

function TextSectionCard({
  eyebrow,
  title,
  children
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <SurfaceCard className="h-full rounded-none">
      <CardContent className="p-6 sm:p-7">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
          {eyebrow}
        </div>
        <h2 className="font-serif text-[clamp(1.7rem,2.4vw,2.2rem)] font-semibold tracking-tight text-gray-950 dark:text-gray-50">
          {title}
        </h2>
        <div className="mt-4 text-[15px] leading-7 text-gray-700 dark:text-gray-300">
          {children}
        </div>
      </CardContent>
    </SurfaceCard>
  );
}

export const LearnStocksDailyPage: React.FC = () => {
  const { data, loading, error } = useLearnStocksDaily();

  if (loading) {
    return (
      <div className="relative mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
        <Helmet>
          <title>Wallstreetle | Learn Stocks Daily</title>
        </Helmet>

        <div className="space-y-4 animate-pulse">
          <div className="h-[250px] rounded-none bg-white/70 shadow-sm dark:bg-white/5 sm:h-[220px]" />
          <div className="h-[320px] rounded-none bg-white/70 shadow-sm dark:bg-white/5" />
          <div className="grid gap-4 md:grid-cols-2">
            <div className="h-56 rounded-none bg-white/70 shadow-sm dark:bg-white/5" />
            <div className="h-56 rounded-none bg-white/70 shadow-sm dark:bg-white/5" />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="h-56 rounded-none bg-white/70 shadow-sm dark:bg-white/5" />
            <div className="h-56 rounded-none bg-white/70 shadow-sm dark:bg-white/5" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="relative mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <Helmet>
          <title>Wallstreetle | Learn Stocks Daily</title>
        </Helmet>

        <SurfaceCard className="rounded-none">
          <CardContent className="p-8 text-center sm:p-10">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-900 text-white dark:bg-white dark:text-gray-900">
              <Building2 className="h-6 w-6" />
            </div>
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
              Learn Stocks Daily
            </h1>
            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-gray-600 dark:text-gray-400">
              {error || "Today's stock is not available yet. Check back soon!"}
            </p>
          </CardContent>
        </SurfaceCard>
      </div>
    );
  }

  const isPositive = (data.priceChangePct ?? 0) >= 0;
  const chartPoints = data.chartData ? data.chartData.slice(-365) : null;
  const chartStroke = isPositive ? '#169c67' : '#dc5b6b';
  const chartGradientId = isPositive ? 'learn-stocks-gradient-up' : 'learn-stocks-gradient-down';
  const oneYearReturn = data.priceChangePct ?? null;
  const isOneYearReturnPositive = (oneYearReturn ?? 0) >= 0;

  return (
    <div className="relative isolate overflow-hidden">
      <Helmet>
        <title>Wallstreetle | Learn Stocks Daily | {data.companyName} ({data.symbol})</title>
        <meta name="description" content={`Learn about ${data.companyName} (${data.symbol}) - ${data.aiContent.overview}`} />
      </Helmet>

      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute left-[-10%] top-10 h-56 w-56 rounded-full bg-emerald-300/18 blur-3xl dark:bg-emerald-500/10" />
        <div className="absolute right-[-8%] top-28 h-64 w-64 rounded-full bg-amber-300/16 blur-3xl dark:bg-amber-500/10" />
        <div className="absolute inset-x-0 top-0 h-64 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.9),transparent_72%)] dark:bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_72%)]" />
      </div>

      <div className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-6 sm:px-6 sm:py-8">
        <SurfaceCard className="rounded-none">
          <CardContent className="p-6 sm:p-8">
            <div className="grid gap-8 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] xl:items-end">
              <div className="min-w-0 max-w-2xl">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-gray-900 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-white dark:bg-white dark:text-gray-900">
                    Learn Stocks Daily
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/75 px-3 py-1 text-xs text-gray-600 ring-1 ring-black/5 dark:bg-white/8 dark:text-gray-300 dark:ring-white/10">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {formatGameDate(data.gameDate)}
                  </span>
                </div>

                <div className="flex items-start gap-4 sm:gap-5">
                  {data.logoUrl ? (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[20px] border border-white/70 bg-white p-2.5 shadow-sm sm:h-20 sm:w-20 dark:border-white/10 dark:bg-white/10">
                      <img
                        src={data.logoUrl}
                        alt={`${data.companyName} logo`}
                        className="h-full w-full rounded-xl object-contain"
                      />
                    </div>
                  ) : null}

                  <div className="min-w-0">
                    <h1 className="font-serif text-[clamp(2.4rem,5vw,4.6rem)] leading-[0.92] tracking-tight text-gray-950 dark:text-gray-50">
                      {data.companyName}
                    </h1>
                    <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                      <span className="rounded-full bg-gray-900/6 px-3 py-1 font-mono font-medium text-gray-700 ring-1 ring-black/5 dark:bg-white/8 dark:text-gray-200 dark:ring-white/10">
                        {data.symbol}
                      </span>
                      <span className="rounded-full bg-gray-900/6 px-3 py-1 text-gray-600 ring-1 ring-black/5 dark:bg-white/8 dark:text-gray-300 dark:ring-white/10">
                        {data.sector}
                      </span>
                      <span className="rounded-full bg-gray-900/6 px-3 py-1 text-gray-600 ring-1 ring-black/5 dark:bg-white/8 dark:text-gray-300 dark:ring-white/10">
                        {data.industry}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-3">
                <div className="rounded-none border border-black/5 bg-white/70 p-5 dark:border-white/10 dark:bg-white/[0.05]">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                    Current Price
                  </div>
                  <div className="font-mono text-2xl font-semibold tracking-tight text-gray-950 dark:text-gray-50">
                    {data.currentPrice != null ? formatPrice(data.currentPrice) : 'N/A'}
                  </div>
                </div>

                <div className="rounded-none border border-black/5 bg-white/70 p-5 dark:border-white/10 dark:bg-white/[0.05]">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                    1 Year Return
                  </div>
                  <div className={cn(
                    'flex items-center gap-2 font-mono text-2xl font-semibold tracking-tight',
                    isOneYearReturnPositive ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'
                  )}>
                    {isOneYearReturnPositive ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
                    {oneYearReturn != null ? formatPercent(oneYearReturn) : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </SurfaceCard>

        <SurfaceCard className="rounded-none">
          <CardContent className="p-6 sm:p-7">
              <div className="mb-5">
                <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-gray-900/6 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-600 ring-1 ring-black/5 dark:bg-white/8 dark:text-gray-300 dark:ring-white/10">
                  <LineChart className="h-3.5 w-3.5" />
                  1Y Price History
                </div>
              <h2 className="font-serif text-[clamp(1.9rem,2.8vw,2.5rem)] font-semibold tracking-tight text-gray-950 dark:text-gray-50">
                Price Chart
              </h2>
            </div>

            {chartPoints && chartPoints.length > 0 ? (
              <>
                <div className="h-[280px] rounded-none bg-[linear-gradient(180deg,rgba(255,255,255,0.4),rgba(255,255,255,0.15))] p-2 sm:h-[340px] dark:bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01))]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartPoints} margin={{ top: 20, right: 12, left: 12, bottom: 12 }}>
                      <defs>
                        <linearGradient id={chartGradientId} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartStroke} stopOpacity={0.28} />
                          <stop offset="95%" stopColor={chartStroke} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" tick={false} axisLine={false} tickLine={false} />
                      <YAxis domain={['auto', 'auto']} hide />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(255,255,255,0.96)',
                          border: '1px solid rgba(15,23,42,0.08)',
                          borderRadius: '16px',
                          boxShadow: '0 18px 40px rgba(15,23,42,0.12)',
                          fontSize: '12px',
                          color: '#111827'
                        }}
                        formatter={(value: number) => [formatPrice(value), 'Price']}
                        labelFormatter={(label: string) =>
                          new Date(label).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        }
                      />
                      <Area
                        type="monotone"
                        dataKey="price"
                        stroke={chartStroke}
                        strokeWidth={2.5}
                        fill={`url(#${chartGradientId})`}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

              </>
            ) : (
              <div className="rounded-none border border-dashed border-black/10 px-5 py-10 text-center text-sm text-gray-500 dark:border-white/10 dark:text-gray-400">
                Historical chart data is unavailable for today&apos;s feature.
              </div>
            )}
          </CardContent>
        </SurfaceCard>

        <div className="grid gap-5 md:grid-cols-2">
          <TextSectionCard eyebrow="Company Snapshot" title="What it does">
            <p>{data.aiContent.overview}</p>
          </TextSectionCard>
          <TextSectionCard eyebrow="Today&apos;s Read" title="Why the stock moved">
            <p>{data.aiContent.priceMovement}</p>
          </TextSectionCard>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <InsightList
            title="What could drive it higher"
            items={data.aiContent.catalysts}
            icon={<ArrowUp className="h-3.5 w-3.5" />}
            tone="up"
          />
          <InsightList
            title="What could pressure it"
            items={data.aiContent.risks}
            icon={<ArrowDown className="h-3.5 w-3.5" />}
            tone="down"
          />
        </div>

        <p className="pb-4 text-center text-xs font-medium uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
          For educational purposes only. Not financial advice.
        </p>
      </div>
    </div>
  );
};

export default LearnStocksDailyPage;
